import "jspdf-autotable";
import Vue from "vue";
import Vuex from "vuex";
import { jsPDF } from "jspdf";
import axios from "@/scripts/axios.js";
import { ApiClient, AssetApi, CartApi, MetadataApi, PermissionsApi, StorageApi, UploadApi, WebhookApi } from "@/http/generated/src/index.js";
import { EventBus } from "@/scripts/event-bus.js";
import { getToken, withAuthToken, getUsername, getRoles, getGroups, isAdmin } from "@/scripts/keycloak.js";
import { installFailureHandling } from "@/scripts/apiErrors.js";
import { startUpload } from "@/scripts/uploads.js";
import { plural } from "@/scripts/plural.js";
import { WIRE_UNSET, displayTc, toFrames, offsetFrames } from "@/scripts/timecode.js";
import { assetTitle } from "@/scripts/assetLabels.js";
import { hasProxy } from "@/scripts/assetStatus.js";
import { proxyUrl, probeDuration } from "@/scripts/media.js";
import { PAGE_LIMIT, SORTS } from "@/scripts/assetView.js";
const Stomp = require("stompjs");
import SockJS from "sockjs-client";

/** The root as an update's parentID; null there means "leave it where it is" (AssetAccessService). */
const ROOT_PARENT = "";

/** A folder chip counts up to this many items and shows more as "99+". */
const FOLDER_COUNT_LIMIT = 100;
/** Folder ids with a count read in flight. */
const countingIds = new Set();

/** Deleting a folder re-parents its children one request each, so the batch stays bounded. */
const FOLDER_CHILD_LIMIT = 300;

/** Asset ids with a whole-asset add or remove in flight, so a double click cannot add twice. */
const inFlight = new Set();

/** Asset ids with a length probe in flight; a failed probe leaves, so it can be asked again. */
const probing = new Set();

/** tus handles by upload entry id. Out of the store: Vue would walk the library's internals. */
const runningUploads = new Map();

/** Last re-read of an asset this client does not hold; an upload in progress updates every second. */
const lastReread = new Map();
const REREAD_EVERY_MS = 10000;
function mayReread(id) {
  const now = Date.now();
  if (now - (lastReread.get(id) || 0) < REREAD_EVERY_MS) return false;
  lastReread.set(id, now);
  return true;
}

/** Folder of assets this client re-read without holding them, so a progress tick is not taken for a move. */
const seenParent = new Map();
/** Whether a re-read asset changed folder; `fallback` decides for one never seen before. */
function movedFolder(stored, asset, fallback) {
  const parent = asset.parentID || null;
  let moved = fallback;
  if (stored) moved = (stored.parentID || null) !== parent;
  else if (seenParent.has(asset.id)) moved = seenParent.get(asset.id) !== parent;
  seenParent.set(asset.id, parent);
  return moved;
}

// A plain copy of a set for the search form: the generated models declare `value` on their prototype,
// which Vue.set cannot make reactive, and the search must not write into the sets the admin edits.
function searchCopy(set) {
  return set ? JSON.parse(JSON.stringify(set)) : undefined;
}

// The metadata set chosen for the search, kept across visits; storage may be unavailable.
const SET_ID_KEY = "selectedMetadataSetId";
function rememberedSetId() {
  try {
    return localStorage.getItem(SET_ID_KEY);
  } catch (e) {
    return null;
  }
}
function rememberSetId(id) {
  try {
    if (id) localStorage.setItem(SET_ID_KEY, id);
    else localStorage.removeItem(SET_ID_KEY);
  } catch (e) {
    // the choice just is not remembered
  }
}

/** Numbers every asset request, so a slow earlier answer never overwrites a newer one. */
let assetRequestSeq = 0;

/** Search while typing: one request once the typing pauses. */
let pendingSearch = null;
function requestAssetsSoon(state) {
  clearTimeout(pendingSearch);
  pendingSearch = setTimeout(() => state.requestAssets(), 350);
}

/** A batch of uploads announces one asset each; one refresh a moment later covers them all. */
let pendingRefresh = null;
function refreshAssetViewSoon(state) {
  clearTimeout(pendingRefresh);
  pendingRefresh = setTimeout(() => state.refreshAssetView(), 300);
}
/** Set while a handshake is in flight, so a second connect never opens a second socket. */
let connecting = false;
/** The one pending reconnect; the error callback and onclose both report the same loss. */
let reconnectTimer = null;
const RECONNECT_FIRST_MS = 5000;
const RECONNECT_MAX_MS = 60000;

Vue.use(Vuex);
const store = new Vuex.Store({
  actions: {
    connect({ commit, dispatch, state }) {
      if (connecting || state.connected) return;
      connecting = true;
      const socket = new SockJS(withAuthToken(process.env.VUE_APP_WS));
      const client = Stomp.over(socket);
      let that = this;
      // Disable console debugging
      client.debug = null;

      client.connect(
        {},
        () => {
          connecting = false;
          // read before SET_CONNECTED, which resets the count
          const reconnected = state.reconnectAttempts > 0;
          commit("SET_CONNECTED", true);
          commit("SET_CLIENT", client);

          client.subscribe("/asset/update", (message) => {
            // The event carries only the id: re-read through the API, which applies permissions.
            const { id } = JSON.parse(message.body);
            const state = that.state;
            const inList = (list) => list.some((obj) => obj.id === id);
            const known = inList(state.clips) || inList(state.cartAssets) || state.currentClip.id === id;
            // ponytail: the server has no move event; an unknown asset is re-read to see if it moved in
            if (!known && (state.hasActiveSearch() || !mayReread(id))) return;

            state.getAssetApi().getAssetsByIds({ requestBody: [id] }, (error, data) => {
              if (error || !data || !data.length) return;
              const asset = data[0];
              const browsing = !state.hasActiveSearch();
              const inOpenFolder = (asset.parentID || null) === (state.getLastFilterParentAsset().id || null);
              const at = state.clips.findIndex((obj) => obj.id === id);
              const inCart = state.cartAssets.findIndex((obj) => obj.id === id);
              if (movedFolder(state.clips[at] || state.cartAssets[inCart], asset, inOpenFolder || asset.parentID in state.folderCounts)) {
                state.clearFolderCounts();
              }
              if (at === -1) {
                if (browsing && inOpenFolder) refreshAssetViewSoon(state);
              } else if (browsing && !inOpenFolder) {
                Vue.delete(state.clips, at);
              } else {
                Vue.set(state.clips, at, asset);
              }
              // a cart clip's asset from another folder lives only in cartAssets
              if (inCart !== -1) Vue.set(state.cartAssets, inCart, asset);
              if (state.currentClip.id === id) state.currentClip = asset;
            });
            console.log("RECEIVED FROM /asset/update WS", id);
          });
          client.subscribe("/asset/add", (message) => {
            // Re-read it: the event carries only the id, and it may belong to another folder.
            const { id } = JSON.parse(message.body);
            that.state.getAssetApi().getAssetsByIds({ requestBody: [id] }, (error, data) => {
              if (error || !data || !data.length) return;
              Vue.delete(that.state.folderCounts, data[0].parentID);
              seenParent.set(id, data[0].parentID || null);
              const parent = that.state.getLastFilterParentAsset();
              if ((data[0].parentID || null) === ((parent && parent.id) || null)) {
                refreshAssetViewSoon(that.state);
              }
            });
            console.log("RECEIVED FROM /asset/add WS", id);
          });
          client.subscribe("/asset/delete", (message) => {
            const asset = JSON.parse(message.body);
            // the event carries only the id and a deleted asset cannot be re-read, so its folder is unknown
            that.state.clearFolderCounts();
            const indexClips = that.state.clips.findIndex((obj) => obj.id === asset.id);
            if (indexClips !== -1) {
              Vue.delete(that.state.clips, indexClips);
            }
            // a reset clears the clip draft, so only the loaded asset's own delete may trigger it
            if (asset.id === that.state.currentClip.id) {
              EventBus.$emit("reset-clip", null);
            }

            const indexCartAssets = that.state.cartAssets.findIndex((obj) => obj.id === asset.id);
            if (indexCartAssets !== -1) {
              Vue.delete(that.state.cartAssets, indexCartAssets);
            }

            console.log("RECEIVED FROM /asset/delete WS", asset);
          });
          client.subscribe("/cart/update", (message) => {
            // The event carries only the id: re-read through the API, which applies permissions.
            const { id } = JSON.parse(message.body);
            const listed = that.state.carts.some((obj) => obj.id === id);
            if (!listed && that.state.cart.id !== id) return;
            that.state.rereadCart(id);
            console.log("RECEIVED FROM /cart/update WS", id);
          });
          client.subscribe("/cart/add", (message) => {
            const { id } = JSON.parse(message.body);
            that.state.getCartApi().getCartById(id, (error, data) => {
              if (!error && data) that.state.addCartToList(data);
            });
          });
          client.subscribe("/cart/delete", (message) => {
            that.state.dropCartFromList(JSON.parse(message.body).id);
          });

          // events sent while the socket was down are lost
          if (reconnected) that.state.resync();
        },
        (error) => {
          connecting = false;
          console.error("WebSocket connection error:", error);
          commit("SET_CONNECTED", false);
          dispatch("attemptReconnect");
        }
      );

      socket.onclose = () => {
        connecting = false;
        console.warn("WebSocket connection closed");
        commit("SET_CONNECTED", false);
        dispatch("attemptReconnect");
      };
    },

    // Never gives up: 5 s, doubling up to a minute between attempts.
    attemptReconnect({ commit, state, dispatch }) {
      if (reconnectTimer) return;
      commit("INCREMENT_RECONNECT_ATTEMPTS");
      const delay = Math.min(RECONNECT_FIRST_MS * 2 ** (state.reconnectAttempts - 1), RECONNECT_MAX_MS);
      console.log(`Reconnection attempt #${state.reconnectAttempts} in ${delay} ms`);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        dispatch("connect");
      }, delay);
    },

    disconnect({ state, commit }) {
      if (state.client) {
        state.client.disconnect(() => {
          commit("SET_CONNECTED", false);
        });
      }
    },
  },
  getters: {},
  modules: {},
  mutations: {
    SET_CLIENT(state, client) {
      state.client = client;
    },
    SET_CONNECTED(state, connected) {
      state.connected = connected;
      if (connected) {
        state.reconnectAttempts = 0; // Reset reconnect attempts when connected
      }
    },
    INCREMENT_RECONNECT_ATTEMPTS(state) {
      state.reconnectAttempts += 1;
    },
  },
  state: {
    client: null,
    connected: false,
    reconnectAttempts: 0,
    addAssetToCart(asset, cartID, done) {
      // a plain "+" is a whole-asset subclip (no in/out)
      this.addSubclip(
        {
          id: this.getRandomUuid(),
          assetID: asset.id,
          cartID: cartID,
          cartName: this.cart.name,
          name: assetTitle(asset),
          note: "",
          subclipStatus: "NEW",
          subclipPriority: "MEDIUM",
          tcIn: WIRE_UNSET,
          tcOut: WIRE_UNSET,
        },
        done
      );
    },

    /** The active cart's clips of an asset; none while no cart is selected. */
    cartClipsOf(assetId) {
      if (!this.cart.id) return [];
      return (this.cart.subclips || []).filter((subclip) => subclip.assetID === assetId);
    },
    isInActiveCart(assetId) {
      return this.cartClipsOf(assetId).length > 0;
    },
    /** The asset's whole-asset entry: the one with neither In nor Out. */
    wholeClipOf(assetId) {
      return this.cartClipsOf(assetId).find((subclip) => subclip.tcIn === WIRE_UNSET && subclip.tcOut === WIRE_UNSET);
    },
    /** The +/✓ button: adds the whole asset, or removes that entry (confirming when it has a comment). */
    toggleWholeAsset(asset) {
      if (!this.cart.id) return this.showErrorDialog("No cart selected.");
      if (inFlight.has(asset.id)) return;
      const send = (request) => {
        inFlight.add(asset.id);
        request(() => inFlight.delete(asset.id));
      };
      const whole = this.wholeClipOf(asset.id);
      if (!whole) return send((done) => this.addAssetToCart(asset, this.cart.id, done));
      const remove = () => send((done) => this.deleteSubclip(whole, done));
      if (whole.note) this.showConfirmDialog(`Remove “${whole.name}” and its comment from “${this.cart.name}”?`, remove);
      else remove();
    },
    cartToggleTitle(assetId) {
      if (!this.cart.id) return "Select a cart first";
      return this.wholeClipOf(assetId) ? `Remove from “${this.cart.name}”` : `Add to “${this.cart.name}”`;
    },

    /** Saves the fields sent (the server keeps the others), then calls done(ok). */
    updateAsset(asset, done) {
      this.getAssetApi().updateAsset({ asset }, (error, data) => {
        if (error) {
          window.logRed("update | asset | error", error);
        } else {
          const index = this.clips.findIndex((obj) => obj.id === data.id);
          if (index !== -1) Vue.set(this.clips, index, data);
          if (this.currentClip.id === data.id) this.currentClip = data;
        }
        done(!error);
      });
    },

    deleteCart(id) {
      if (!id) {
        return;
      }
      window.logOrange("request | delete cart", id);
      this.getCartApi().removeCart(id, {}, (error, data, response) => {
        if (error) {
          window.logRed("request | delete cart | error", error);
        } else {
          window.logGreen("request | delete cart | success", {});
          this.dropCartFromList(id);
        }
      });
    },
    // PUT /cart replaces the stored document for most statuses, so it must carry the whole cart.
    renameCart(cart, name) {
      if (!name) {
        return;
      }
      const opts = { overwrite: false, cart: { ...cart, name } };
      window.logOrange("request | rename cart", opts);
      this.getCartApi().updateCart(opts, (error, data) => {
        if (error) {
          window.logRed("request | rename cart | error", error);
        } else {
          window.logGreen("request | rename cart | success", data);
          this.updateCartInCarts(data);
        }
      });
    },
    /** Creates a cart for the signed-in user and opens it. */
    addCart(name) {
      const cartName = (name || "").trim();
      if (!cartName) return;
      const opts = { overwrite: false, cart: { name: cartName, user: this.authUser.username } };
      window.logOrange("request | add cart", opts);
      this.getCartApi().addCart(opts, (error, data) => {
        if (error) {
          window.logRed("request | add cart | error", error);
        } else {
          window.logGreen("request | add cart | success", data);
          this.addCartToList(data);
          this.selectCart(data);
        }
      });
    },

    /** done(ok), if given, runs once: true after the add, false on an error or with no cart selected. */
    addSubclip(subclipToAdd, done = () => {}) {
      if (!this.cart.id) {
        this.showErrorDialog("No cart selected.");
        done(false);
        return;
      }
      let subclipObj = Object.assign({}, subclipToAdd);
      let foundName = false;
      let newName = subclipToAdd.name;
      let i = 2;

      do {
        if (!this.getSubclipsAsArray().length) {
          foundName = true;
          break;
        }

        if (!this.getSubclipByName(newName)) {
          foundName = true;
          break;
        }

        newName = subclipObj.name + "_" + i++;
      } while (!foundName);

      subclipObj.name = newName;
      this.axiosCall(
        "put",
        "/cart/subclip/add/" + this.cart.id,
        [subclipObj],
        (response) => {
          if (response.data) {
            this.updateCartInCarts(response.data);
            setTimeout(function () {
              EventBus.$emit("subcliplist-scroll-bottom");
            }, 50);
          }
          done(true);
        },
        () => done(false)
      );
    },

    apiClient: installFailureHandling(new ApiClient(process.env.VUE_APP_API_ENDPOINT + "/v1")),
    apiEndpoint: process.env.VUE_APP_API_ENDPOINT + "/v1",

    axiosCall(method, queryString, body, callback, errorCallback) {
      // The path stays relative: axios.js owns the /v1 prefix as its baseURL.
      axios[method.toLowerCase()](queryString, body)
        .then((response) => {
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          if (errorCallback) {
            errorCallback(error);
          }
        });
    },

    cart: {
      default: {},
    },
    carts: [],
    cartAssets: [],

    clipLoadingAniRefHeight: "",
    clips: [],
    clipsRefMaxHeight: "",
    currentClip: {},
    currentSubclip: {},

    deleteClip(clipToDelete) {
      this.axiosCall("put", "/cart/asset/remove/" + this.cart.id, [clipToDelete.id], (response) => {
        if (response.data) {
          this.cart = response.data;
          this.refreshCartContent(this.cart);
        }
      });
    },

    /** done(ok), if given, runs once: true after the removal, false on an error. */
    deleteSubclip(subclipToDelete, done = () => {}) {
      this.axiosCall(
        "put",
        "/cart/subclip/remove/" + this.cart.id,
        [subclipToDelete.id],
        (response) => {
          if (response.data) {
            this.updateCartInCarts(response.data);
            if (this.currentSubclip.id == subclipToDelete.id) EventBus.$emit("reset-clip", null);
          }
          done(true);
        },
        () => done(false)
      );
    },

    dialog: {
      buttonCancelText: "Cancel",
      buttonConfirmText: "Ok",
      cancelCallback: () => {},
      confirmCallback: () => {},
      text: "",
      title: "",
      prompt: "",
      visible: false,
    },

    downloadPDF() {
      let pdf = new jsPDF();
      let tableBody = [];

      for (let i in this.cart.subclips) {
        let subclip = this.cart.subclips[i];
        let clip = this.getClipById(subclip.assetID);
        let note = subclip.note;
        if (!note) note = "";
        tableBody.push([clip ? clip.tapeID : "", subclip.name, note, displayTc(subclip.tcIn), displayTc(subclip.tcOut)]);
      }

      pdf.text(15, 10, this.cart.name);
      pdf.autoTable({
        body: tableBody,
        columnStyles: {
          0: {
            cellWidth: 25,
          },
          1: {
            cellWidth: 50,
          },
          2: {
            cellWidth: "auto",
          },
          3: {
            cellWidth: "auto",
          },
          4: {
            cellWidth: "auto",
          },
        },
        head: [["TapeID", "Title", "Notiz", "In", "Out"]],
      });
      pdf.save(this.cart.name + "_" + this.formatDateForPDF(new Date()) + ".pdf");
      pdf.output("dataurlnewwindow"); // pdf.output('pdfobjectnewwindow');
    },

    fileServlet: process.env.VUE_APP_API_ENDPOINT + "/v1",
    filterParentAssets: [
      {
        title: "root",
      },
    ],

    formatDateForPDF(date) {
      function pad(n) {
        return (n < 10 ? "0" : "") + n;
      }

      let yyyy = date.getFullYear().toString();
      let MM = pad(date.getMonth() + 1);
      let dd = pad(date.getDate());
      let hh = pad(date.getHours());
      let mm = pad(date.getMinutes());
      let ss = pad(date.getSeconds());
      return yyyy + "." + MM + "." + dd + "-" + hh + "." + mm + "." + ss;
    },

    get defaultHeaders() {
      // header values must be strings: superagent crashes on arrays when serializing DELETE bodies
      const headers = {
        Accept: "application/json, text/html",
        "Content-Type": "application/json",
      };
      headers["Authorization"] = "Bearer " + getToken();
      return headers;
    },

    getAssetApi() {
      this.apiClient.defaultHeaders = this.defaultHeaders;
      return new AssetApi(this.apiClient);
    },

    getCartApi() {
      this.apiClient.defaultHeaders = this.defaultHeaders;
      return new CartApi(this.apiClient);
    },

    getPermissionsApi() {
      this.apiClient.defaultHeaders = this.defaultHeaders;
      return new PermissionsApi(this.apiClient);
    },

    adminOpen: false,

    metadataSets: [],
    getMetadataApi() {
      this.apiClient.defaultHeaders = this.defaultHeaders;
      return new MetadataApi(this.apiClient);
    },

    // Persist a metadata set: POST when new (no id), PUT otherwise, then refresh.
    saveMetadataSet(set, callback) {
      const isNew = !set.id;
      const opts = { metadataSet: set };
      const cb = (error, data) => {
        if (error) {
          window.logRed("request | save MetadataSet | error", error);
        } else {
          window.logGreen("request | save MetadataSet | success", data);
          this.requestMetadataSets();
        }
        if (callback) callback(error, data);
      };
      window.logOrange("request | save MetadataSet", opts);
      if (isNew) this.getMetadataApi().addMetadataSet(opts, cb);
      else this.getMetadataApi().updateMetadataSet(opts, cb);
    },

    getWebhookApi() {
      this.apiClient.defaultHeaders = this.defaultHeaders;
      return new WebhookApi(this.apiClient);
    },

    loadWebhookConfig(callback) {
      window.logOrange("request | webhook config");
      this.getWebhookApi().getWebhookConfig((error, data) => {
        if (error) window.logRed("request | webhook config | error", error);
        else window.logGreen("request | webhook config | success", data);
        if (callback) callback(error, data);
      });
    },

    // The body is not logged: it may carry the signing secret.
    saveWebhookConfig(config, callback) {
      window.logOrange("request | save webhook config");
      this.getWebhookApi().updateWebhookConfig(config, (error, data) => {
        if (error) window.logRed("request | save webhook config | error", error);
        else window.logGreen("request | save webhook config | success", data);
        if (callback) callback(error, data);
      });
    },

    getStorageApi() {
      this.apiClient.defaultHeaders = this.defaultHeaders;
      return new StorageApi(this.apiClient);
    },

    loadStorage(callback) {
      window.logOrange("request | storage");
      this.getStorageApi().getStorage((error, data) => {
        if (error) window.logRed("request | storage | error", error);
        else window.logGreen("request | storage | success", data);
        if (callback) callback(error, data);
      });
    },

    getUploadApi() {
      this.apiClient.defaultHeaders = this.defaultHeaders;
      return new UploadApi(this.apiClient);
    },

    loadUploadConfig(callback) {
      window.logOrange("request | upload config");
      this.getUploadApi().getUploadConfig((error, data) => {
        if (error) window.logRed("request | upload config | error", error);
        else window.logGreen("request | upload config | success", data);
        if (callback) callback(error, data);
      });
    },

    saveUploadConfig(config, callback) {
      window.logOrange("request | save upload config", config);
      this.getUploadApi().updateUploadConfig(config, (error, data) => {
        if (error) window.logRed("request | save upload config | error", error);
        else window.logGreen("request | save upload config | success", data);
        if (callback) callback(error, data);
      });
    },

    uploadOpen: false,
    uploadConfig: null,
    // { id, name, size, progress, status: uploading|paused|done|error, error }
    uploads: [],

    /** One asset per file, all with the same metadata; the entries outlive the dialog. */
    queueUploads(files, metadata) {
      const parentID = this.getLastFilterParentAsset().id || null;
      for (const file of files) {
        const entry = { id: this.getRandomUuid(), name: file.name, size: file.size, progress: 0, status: "uploading", error: "" };
        this.uploads.push(entry);
        runningUploads.set(entry.id, startUpload(file, { parentID, metadata }, this.uploadTracker(entry)));
      }
    },

    /** The entry is already in the reactive list, so the upload reports straight into it. */
    uploadTracker(entry) {
      return {
        onProgress: (sent, total) => {
          entry.progress = total ? Math.floor((sent * 100) / total) : 0;
        },
        onSuccess: () => {
          entry.progress = 100;
          entry.status = "done";
        },
        onError: (message) => {
          entry.status = "error";
          entry.error = message;
        },
      };
    },

    /** Aborting without terminating keeps the offset, so resuming continues where it stopped. */
    pauseUpload(entry) {
      const upload = runningUploads.get(entry.id);
      if (!upload) return;
      upload.abort();
      entry.status = "paused";
    },

    resumeUpload(entry) {
      const upload = runningUploads.get(entry.id);
      if (!upload) return;
      entry.status = "uploading";
      entry.error = "";
      upload.start();
    },

    /** Terminating tells the server to drop the upload, which takes the placeholder with it. */
    cancelUpload(entry) {
      const upload = runningUploads.get(entry.id);
      if (upload) upload.abort(true);
      runningUploads.delete(entry.id);
      const at = this.uploads.indexOf(entry);
      if (at !== -1) this.uploads.splice(at, 1);
    },

    /** Only the rows that will not change again; a failed one can still be resumed. */
    clearFinishedUploads() {
      this.uploads = this.uploads.filter((entry) => {
        if (entry.status !== "done") return true;
        runningUploads.delete(entry.id);
        return false;
      });
    },

    getAssetsByIds(ids) {
      window.logOrange("request | assets by Ids");
      this.getAssetApi().getAssetsByIds(
        {
          requestBody: ids,
        },
        (error, data, response) => {
          if (error) {
            window.logRed("request | assets by Ids | error", error);
          } else {
            window.logGreen("request | assets by Ids | success", data);
            this.clips = data.assets;
          }
        }
      );
    },

    getClipById(id) {
      if (!this.clips) return null;
      // include cart assets so subclips play even when not in the current search
      let allClips = this.clips.concat(this.hiddenClips).concat(this.cartAssets || []);

      for (let i in allClips) {
        let clip = allClips[i];
        if (clip.id == id) return clip;
      }

      return null;
    },

    getClipsFilteredByParentAsset() {
      if (this.getLastFilterParentAsset().id) {
        return this.showedContent.clips;
      } else {
        return this.clips;
      }
    },

    /** Opens a breadcrumb level and re-reads it, also when it is the level already open. */
    getFilteredAssets(index) {
      this.filterParentAssets.splice(index + 1, this.filterParentAssets.length);
      this.clearFolderCounts();
      const folder = this.getLastFilterParentAsset();
      if (folder && folder.id) this.refreshAssetView();
      else this.requestAssets();
    },

    getLastFilterParentAsset() {
      return this.filterParentAssets[this.filterParentAssets.length - 1];
    },

    getRandomUuid() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        let r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16).toUpperCase();
      });
    },

    getSubclipByName(name) {
      for (let i in this.getSubclipsAsArray()) {
        let subclip = this.getSubclipsAsArray()[i];
        if (subclip.name == name) return subclip;
      }

      return null;
    },

    getSubclipsAsArray() {
      let output = [];

      for (let i in this.cart.subclips) {
        output.push(this.cart.subclips[i]);
      }

      return output;
    },

    getProxyUrl(id) {
      return proxyUrl(id);
    },

    /** Proxy lengths in seconds by asset id; the API has none. */
    durations: {},
    durationOf(id) {
      return this.durations[id];
    },
    rememberDuration(id, secs) {
      if (Number.isFinite(secs) && secs > 0) Vue.set(this.durations, id, secs);
    },
    /** Probes an asset's length once; skipped until the asset has a proxy (a dummy's length would stick). */
    requestDuration(asset) {
      if (!hasProxy(asset) || this.durations[asset.id] || probing.has(asset.id)) return;
      probing.add(asset.id);
      probeDuration(asset.id).then((secs) => {
        probing.delete(asset.id);
        this.rememberDuration(asset.id, secs);
      });
    },

    // The url has to change with the thumbnail, or the img keeps showing the placeholder it
    // loaded while the asset was still uploading.
    getThumbUrl(id, thumbnail) {
      const url = withAuthToken(this.apiEndpoint + "/fileServlet/thumb?id=" + id);
      return thumbnail ? url + "&v=" + encodeURIComponent(thumbnail) : url;
    },

    hiddenClips: [],
    notes: [],

    playClip(clip) {
      this.currentClip = clip;
      this.currentSubclip = {};
      EventBus.$emit("play-clip", clip);
    },

    playSubclip(subclip) {
      let clip = this.getClipById(subclip.assetID);
      if (!clip) return;
      this.currentClip = clip;
      this.currentSubclip = subclip;

      EventBus.$emit("play-subclip", subclip);
    },

    refreshCart() {
      if (this.cart) {
        let oldCart = Object.assign({}, this.cart);
        this.axiosCall("get", "/cart/" + this.cart.id, null, (response) => {
          if (response && response.data) {
            this.refreshCartContent(response.data);
          } else {
            this.showErrorDialog("No Cart found with ID: " + this.cart.id);
            this.cart = oldCart;
          }
        });
      }
    },

    refreshCartContent(cart) {
      window.log("refreshCartContent", cart);

      if (cart.cartStatus.toUpperCase() == "LOCKED") {
        this.cart = {
          name: "Cart locked",
        };
      } else if (cart.cartStatus.toUpperCase() == "IN USE") {
        this.cart = cart;
      } else {
        this.cart = cart;
        this.clips = [];
        let clipIDs = cart.assets;

        if (clipIDs && clipIDs.length) {
          this.axiosCall("post", "/asset", clipIDs, (response) => {
            this.clips = response.data;
          });
        }

        this.hiddenClips = [];
        let hiddenClipIDs = cart.hiddenAssets;

        if (hiddenClipIDs && hiddenClipIDs.length) {
          this.axiosCall("post", "/asset", hiddenClipIDs, (response) => {
            this.hiddenClips = response.data;
          });
        }
      }
    },

    search: {
      text: "",
      date: undefined,
      scope: "global",
      kind: "all",
      sort: "newest",
      metadataSet: undefined,
    },

    requestAssetsSoon() {
      requestAssetsSoon(this);
    },

    requestAssets() {
      clearTimeout(pendingSearch);
      if (!this.search.text?.length) this.search.text = "";

      // browse mode (no filters): only root-level assets, so folders behave as containers
      if (!this.hasActiveSearch()) {
        this.refreshAssetView();
        return;
      }

      let opts = {
        searchRequest: {
          page: 0,
          pageSize: PAGE_LIMIT,
          sortField: SORTS[this.search.sort].field,
          sortOrder: SORTS[this.search.sort].order,
          searchCriteria: [],
        },
      };

      // any metadata value ("" field), title, TapeID or filename; the scope narrows on the client
      for (const field of ["", "title", "tapeID", "name"]) {
        opts.searchRequest.searchCriteria.push({ field, operator: "or", type: "STRING", value: this.search.text });
      }

      if (this.search.date)
        opts.searchRequest.searchCriteria.push({
          field: "created",
          operator: "and",
          type: "DATE",
          value: this.search.date,
        });

      for (const { item, value } of this.metadataCriteria()) {
        opts.searchRequest.searchCriteria.push({ field: item.label, operator: "and", type: item.type, value });
      }

      window.logOrange("request | assets", JSON.stringify(opts.searchRequest.searchCriteria), opts);

      const seq = ++assetRequestSeq;
      this.getAssetApi().searchAsset(opts, (error, data) => {
        if (seq !== assetRequestSeq) return;
        if (error) {
          window.logRed("request | assets | error", error);
        } else {
          window.logGreen("request | assets | success", data);
          this.clips = data.assets;
        }
      });
    },

    // Legacy cart.assets plus every clip's asset: subclip adds never fill cart.assets.
    cartAssetIds(cart) {
      const ids = (cart.assets || []).concat((cart.subclips || []).map((s) => s.assetID));
      return [...new Set(ids.filter(Boolean))].sort();
    },

    /** Loads the assets behind the cart's clips, so they play and resolve from any folder. */
    requestCartAssets(cart) {
      const ids = this.cartAssetIds(cart);
      if (!ids.length) {
        this.cartAssets = [];
        return;
      }
      window.logOrange("request | cart assets", ids);
      this.getAssetApi().getAssetsByIds({ requestBody: ids }, (error, data) => {
        if (error) {
          window.logRed("request | cart assets | error", error);
        } else if (cart.id === this.cart.id) {
          window.logGreen("request | cart assets | success", data);
          this.cartAssets = data;
        }
      });
    },

    /** Whose carts the list shows: "" for everyone's, else a username (the Mine switch). */
    cartsOwner: "",
    requestCarts(searchText = "", user = this.cartsOwner, limit = "") {
      let opts = {
        limit: limit,
        search: searchText,
        user: user,
      };
      window.logOrange("request | carts", opts);
      this.getCartApi().getCarts(opts, (error, data, response) => {
        if (error) {
          window.logRed("request | carts | error", error);
        } else {
          window.logGreen("request | carts | success", {
            carts: data,
          });
          this.carts = data;
          // the list and the detail then share one object; clips added meanwhile get their assets
          const open = data.find((cart) => cart.id === this.cart.id);
          if (open) this.replaceOpenCart(open);
        }
      });
    },

    // Identity comes from the access token; the server is the authority on what it allows.
    authUser: {
      username: undefined,
      admin: false,
      rootPermission: "NONE",
      roles: [],
      groups: [],
    },

    loadAuthUser() {
      this.authUser.username = getUsername();
      this.authUser.admin = isAdmin();
      this.authUser.roles = getRoles();
      this.authUser.groups = getGroups();
      localStorage.user = this.authUser.username;

      this.getPermissionsApi().getCallerPermissions((error, data) => {
        if (error) {
          window.logRed("request | permissions/me | error", error);
          return;
        }
        this.authUser.username = data.username || this.authUser.username;
        this.authUser.admin = !!data.admin;
        this.authUser.rootPermission = data.rootPermission || "NONE";
        this.authUser.roles = data.roles || this.authUser.roles;
        this.authUser.groups = data.groups || this.authUser.groups;
      });
    },

    permissionsDialog: {
      visible: false,
      loading: false,
      saving: false,
      assetId: undefined,
      assetTitle: "",
      grants: [],
      inherited: [],
      availableGroups: [],
      availableRoles: [],
    },

    openPermissionsDialog(asset) {
      const dialog = this.permissionsDialog;
      dialog.visible = true;
      dialog.loading = true;
      dialog.assetId = asset.id;
      dialog.assetTitle = asset.title || asset.name || asset.id;
      dialog.grants = [];
      dialog.inherited = [];

      this.getPermissionsApi().getAssetPermissions(asset.id, (error, data) => {
        dialog.loading = false;
        if (error) {
          window.logRed("request | permissions | error", error);
          dialog.visible = false;
          return;
        }
        dialog.grants = (data.grants || []).map((g) => ({ ...g }));
        dialog.inherited = data.inherited || [];
      });

      // Only admins may enumerate principals; everyone else types them in.
      if (this.authUser.admin) {
        this.getPermissionsApi().getAvailablePrincipals((error, data) => {
          if (error) return;
          dialog.availableGroups = data.groups || [];
          dialog.availableRoles = data.roles || [];
        });
      }
    },

    savePermissions() {
      const dialog = this.permissionsDialog;
      const grants = dialog.grants.filter((g) => g.principalName && g.principalName.length);
      dialog.saving = true;
      this.getPermissionsApi().setAssetPermissions(dialog.assetId, { permissionGrant: grants }, (error) => {
        dialog.saving = false;
        if (error) {
          window.logRed("request | permissions save | error", error);
          return;
        }
        dialog.visible = false;
        this.refreshAssetView();
      });
    },

    // Permission ranking mirrors the server's PermissionLevel.
    permissionRank(level) {
      return { NONE: 0, VIEW: 1, EDIT: 2, MANAGE: 3 }[level] || 0;
    },
    can(level, asset) {
      if (this.authUser.admin) return true;
      if (!asset) return false;
      return this.permissionRank(asset.effectivePermission) >= this.permissionRank(level);
    },
    canEdit(asset) {
      return this.can("EDIT", asset);
    },
    canManage(asset) {
      return this.can("MANAGE", asset);
    },
    /** Whether the caller may add to / rearrange the given container; null means the root. */
    canEditIn(folder) {
      if (this.authUser.admin) return true;
      if (!folder || folder.title === "root") {
        return this.permissionRank(this.authUser.rootPermission) >= this.permissionRank("EDIT");
      }
      return this.canEdit(folder);
    },

    initRequests() {
      this.requestAssets();
      this.requestCarts();
      this.requestMetadataSets();
      this.loadUploadConfig((error, data) => {
        if (!error) this.uploadConfig = data;
      });
    },

    // ponytail: All | Mine replaced the admin's any-user picker; bring it back with getAllUserNames
    // (UserApi) feeding cartsOwner if admins miss it.
    requestMetadataSets() {
      window.logOrange("request | all MetadataSets");
      this.getMetadataApi().getAllMetadataSets((error, data, response) => {
        if (error) {
          window.logRed("request | all MetadataSets | error", error);
        } else {
          window.logGreen("request | all MetadataSets | success", data);
          this.metadataSets = data;
          const remembered = rememberedSetId();
          if (remembered) this.search.metadataSet = searchCopy(data.find((set) => set.id === remembered));
        }
      });
    },

    resetDialog() {
      this.dialog.visible = false;
      this.dialog.title = "";
      this.dialog.text = "";
      this.dialog.buttonConfirmText = "";
      this.dialog.buttonCancelText = "";
      this.dialog.prompt = "";

      this.dialog.confirmCallback = () => {};

      this.dialog.cancelCallback = () => {};
    },

    restoreCart(cartId) {
      window.log("RESTORING CART", cartId);
      this.getCartApi().restoreCartById(cartId, (error, data, response) => {
        if (error) {
          window.logRed("request | restoreCart | error", error);
        } else {
          window.log("RESPONSE RESTORE CART", data);
          this.updateCartInCarts(data);
        }
      });
    },

    /** The cart being submitted, so Start restore turns to 'Restoring…' before the server answers. */
    submittingId: "",
    restoreClips() {
      const id = this.cart.id;
      this.submittingId = id;
      const clear = () => {
        if (this.submittingId === id) this.submittingId = "";
      };
      // submit broadcasts only the first status change, so the answer is followed by a re-read
      this.axiosCall("post", "/cart/submit", this.cart, () => this.rereadCart(id, clear), clear);
    },

    setParentAsset(asset) {
      window.log("set Parent Asset", asset);

      if (!asset.id) {
        this.refreshCart();
      } else if (!this.getLastFilterParentAsset() || this.getLastFilterParentAsset().id != asset.id) {
        this.filterParentAssets.push(asset);
        this.clearFolderCounts();
        this.refreshAssetView();
      }
    },

    /** Back to the plain folder listing: no text, criteria, type or scope. */
    clearSearch() {
      this.search.text = "";
      this.clearMetadataCriteria();
      this.search.kind = "all";
      this.search.scope = "global";
      this.requestAssets();
    },

    /** Catches up on what live events may have missed: after a reconnect or a hidden tab. */
    resync() {
      this.clearFolderCounts();
      this.refreshAssetView();
      this.requestCarts();
    },

    /** Item counts of the folder chips, by folder id; the API has no count field. */
    folderCounts: {},
    // ponytail: one capped read per chip. A move by another user between two folders this client does
    // not show goes unseen; the count catches up on the next folder change or resync.
    countFolderChildren(ids) {
      for (const id of ids) {
        if (id in this.folderCounts || countingIds.has(id)) continue;
        countingIds.add(id);
        this.getAssetApi().getAssetsWithNameStartingWith({ limit: FOLDER_COUNT_LIMIT, searchParentID: id }, (error, data) => {
          countingIds.delete(id);
          if (!error && data) Vue.set(this.folderCounts, id, (data.assets || []).length);
        });
      }
    },
    clearFolderCounts() {
      this.folderCounts = {};
    },

    hasActiveSearch() {
      return !!(this.search.text || this.search.date || this.metadataCriteria().length);
    },

    /** The chosen set's filled fields as [{ item, value }], one entry per value of a list field. */
    metadataCriteria() {
      const items = (this.search.metadataSet && this.search.metadataSet.metadata) || [];
      return items.flatMap((item) => {
        const values = Array.isArray(item.value) ? item.value : item.value ? [item.value] : [];
        return values.map((value) => ({ item, value }));
      });
    },

    /** Picks the metadata set to search by, and remembers it for the next visit. */
    selectMetadataSet(id) {
      const hadCriteria = this.metadataCriteria().length > 0;
      this.search.metadataSet = searchCopy(id && this.metadataSets.find((set) => set.id === id));
      rememberSetId(id);
      if (hadCriteria) this.requestAssets();
    },
    removeMetadataCriterion({ item, value }) {
      Vue.set(item, "value", Array.isArray(item.value) && item.value.length > 1 ? item.value.filter((v) => v !== value) : undefined);
    },
    clearMetadataCriteria() {
      for (const item of (this.search.metadataSet && this.search.metadataSet.metadata) || []) Vue.set(item, "value", undefined);
    },

    // lists the current folder level (root or parent folder) with typed models
    refreshAssetView() {
      const parent = this.getLastFilterParentAsset();
      // a search stays a search at any folder level; the Local scope filters its hits
      if (this.hasActiveSearch()) {
        this.requestAssets();
        return;
      }
      const opts = {
        limit: PAGE_LIMIT,
        sortBy: SORTS[this.search.sort].field,
        sortDirection: SORTS[this.search.sort].order,
      };
      if (parent && parent.id) opts.searchParentID = parent.id;
      window.logOrange("request | assets by folder", opts);
      const seq = ++assetRequestSeq;
      this.getAssetApi().getAssetsWithNameStartingWith(opts, (error, data) => {
        if (seq !== assetRequestSeq) return;
        if (error) {
          window.logRed("request | assets by folder | error", error);
        } else {
          window.logGreen("request | assets by folder | success", data);
          this.clips = data.assets;
        }
      });
    },

    createFolder() {
      // prefill required: DialogComponent only renders the input for a truthy prompt
      this.showPromptDialog("New folder name:", "New Folder", (name) => {
        if (!name) return;
        const asset = {
          id: this.getRandomUuid(),
          title: name,
          folder: true,
          parentID: this.getLastFilterParentAsset().id || null,
        };
        window.logOrange("request | add folder", asset);
        this.getAssetApi().addAsset({ overwrite: false, asset: asset }, (error, data) => {
          if (error) {
            window.logRed("request | add folder | error", error);
          } else {
            window.logGreen("request | add folder | success", data);
            this.refreshAssetView();
          }
        });
      });
    },

    renameFolder(folder) {
      // the field only renders for a truthy prompt, so a folder without a title shows its name
      const current = assetTitle(folder);
      this.showPromptDialog("Rename folder:", current, (name) => {
        if (!name || name === current) return;
        folder.title = name;
        window.logOrange("request | rename folder", folder);
        this.getAssetApi().updateAsset({ asset: folder }, (error, data) => {
          if (error) window.logRed("request | rename folder | error", error);
          else window.logGreen("request | rename folder | success", data);
        });
      });
    },

    deleteFolder(folder) {
      // children move up to the folder's parent, otherwise deleting would orphan them
      this.getAssetApi().getAssetsWithNameStartingWith({ limit: FOLDER_CHILD_LIMIT, searchParentID: folder.id }, (error, data) => {
        if (error) return window.logRed("request | folder children | error", error);
        const children = data.assets || [];
        if (children.length >= FOLDER_CHILD_LIMIT) {
          return this.showErrorDialog(`"${folder.title}" holds too many items to delete at once. Move its contents first.`);
        }
        const note = children.length ? ` ${plural(children.length, "item")} will move to the parent level.` : "";
        this.showConfirmDialog(`Delete folder "${folder.title}"?` + note, () => {
          const removeFolder = () => {
            window.logOrange("request | delete folder", folder);
            this.getAssetApi().removeAsset(folder.id, { deleteMedia: false }, (err, ok) => {
              if (err) window.logRed("request | delete folder | error", err);
              else window.logGreen("request | delete folder | success", ok);
              // a deleted folder that is open, or above the open one, leaves the breadcrumb
              const at = this.filterParentAssets.findIndex((crumb) => crumb.id === folder.id);
              if (at > 0) this.filterParentAssets.splice(at);
              this.refreshAssetView();
            });
          };
          let pending = children.length;
          let failed = false;
          if (!pending) return removeFolder();
          for (const child of children) {
            child.parentID = folder.parentID || ROOT_PARENT;
            this.getAssetApi().updateAsset({ asset: child }, (err) => {
              if (err) {
                window.logRed("request | move folder child | error", err);
                failed = true;
              }
              if (--pending > 0) return;
              // Deleting now would orphan whatever failed to move.
              if (failed) {
                this.showErrorDialog(`"${folder.title}" was not deleted: some items could not be moved.`);
                this.refreshAssetView();
              } else {
                removeFolder();
              }
            });
          }
        });
      });
    },

    cutAssets: [],
    cutAsset(asset) {
      this.cutAssets = [asset];
    },
    pasteCutAssets() {
      this.moveAssets(this.cutAssets, this.getLastFilterParentAsset().id || null);
    },

    moveAssets(assets, targetParentID) {
      // cycle guard: never move an asset into itself or its own subtree
      const targetChain = this.filterParentAssets.map((a) => a.id).concat(targetParentID);
      const targets = (assets || []).filter((a) => a && !targetChain.includes(a.id));
      this.cutAssets = [];
      if (!targets.length) return;

      let pending = targets.length;
      for (const asset of targets) {
        Vue.delete(this.folderCounts, asset.parentID);
        Vue.delete(this.folderCounts, targetParentID);
        asset.parentID = targetParentID || ROOT_PARENT;
        window.logOrange("request | move asset", asset);
        this.getAssetApi().updateAsset({ asset: asset }, (error, data) => {
          if (error) window.logRed("request | move asset | error", error);
          else window.logGreen("request | move asset | success", data);
          if (--pending === 0) this.refreshAssetView();
        });
      }
    },

    showPromptDialog(title, prompt, callback, cancelCallback) {
      this.resetDialog();
      this.dialog.visible = true;
      this.dialog.buttonConfirmText = "Ok";
      this.dialog.buttonCancelText = "Cancel";
      this.dialog.title = title;
      this.dialog.prompt = prompt;
      this.dialog.confirmCallback = callback;
      if (cancelCallback) this.dialog.cancelCallback = cancelCallback;
    },
    showConfirmDialog(text, callback, cancelCallback) {
      this.resetDialog();
      this.dialog.visible = true;
      this.dialog.buttonConfirmText = "Ok";
      this.dialog.buttonCancelText = "Cancel";
      this.dialog.title = "Please confirm";
      this.dialog.text = text;
      this.dialog.confirmCallback = callback;
      if (cancelCallback) this.dialog.cancelCallback = cancelCallback;
    },

    showedContent: {
      clips: [],
      hiddenClips: [],
    },

    showErrorDialog(text) {
      this.resetDialog();
      this.dialog.visible = true;
      this.dialog.buttonConfirmText = "Ok";
      this.dialog.title = "Error";
      this.dialog.text = text;
    },

    subclipsRefMaxHeight: "",

    addCartToList(cart) {
      if (this.cartsOwner && cart.user !== this.cartsOwner) return;
      if (!this.carts.some((obj) => obj.id === cart.id)) this.carts.unshift(cart);
    },
    dropCartFromList(id) {
      const index = this.carts.findIndex((obj) => obj.id === id);
      if (index !== -1) this.carts.splice(index, 1);
      if (this.cart.id === id) {
        this.cart = {};
        this.cartAssets = [];
      }
    },
    /** Opens a cart; re-selecting the open one keeps its loaded assets. */
    selectCart(cart) {
      const previous = this.cart.id;
      this.cart = this.carts.find((obj) => obj.id === cart.id) || cart;
      if (cart.id === previous) return;
      this.cartAssets = [];
      if (this.cartAssetIds(this.cart).length) this.requestCartAssets(this.cart);
    },

    /** Puts a newer copy of a cart into the list and, when it is open, into the detail. */
    updateCartInCarts(cart) {
      const index = this.carts.findIndex((obj) => obj.id === cart.id);
      if (index !== -1) Vue.set(this.carts, index, cart);
      if (cart.id === this.cart.id) this.replaceOpenCart(cart);
    },

    // Swap in a newer copy of the open cart; reload its assets only when its clip set changed.
    replaceOpenCart(cart) {
      const idsChanged = this.cartAssetIds(cart).join() !== this.cartAssetIds(this.cart).join();
      this.cart = cart;
      if (idsChanged) this.requestCartAssets(cart);
    },

    // By id, not getCartsByIds: the server's findAllByIdOrderByCreatedDesc returns no carts.
    rereadCart(id, done) {
      this.getCartApi().getCartById(id, (error, data) => {
        if (!error && data) this.updateCartInCarts(data);
        if (done) done();
      });
    },

    updateSubclip(subclip) {
      this.axiosCall("put", "/subclip", subclip, () => {});
    },

    // For cartTotals: only lengths already known and offsets of loaded assets; the rest stays null,
    // since an unloaded asset's offset read as 0 would give wrong, even negative, lengths.
    clipLookups() {
      return {
        durationFrames: (id) => (this.durations[id] ? toFrames(this.durations[id]) : null),
        offsetFrames: (id) => {
          const asset = this.getClipById(id);
          return asset ? offsetFrames(asset) : null;
        },
      };
    },

    videoContainerRefMaxHeight: "",
    videoRefMaxHeight: "",
  },
});

// Back online: reconnect now instead of waiting out the backoff.
window.addEventListener("online", () => {
  clearTimeout(reconnectTimer);
  reconnectTimer = null;
  store.dispatch("connect");
});

export default store;
